import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { 
  Anchor,
  LogOut, 
  RefreshCw, 
  Loader2,
  Phone,
  Mail,
  User,
  ArrowLeft,
  History,
  ChevronDown,
  ChevronUp,
  Search,
  Users,
  Trash2,
  Ship,
  Edit,
  X,
  UserCheck,
  UserX
} from "lucide-react";
import axios from "axios";
import PhoneInput, { getLanguageFromCountryCode, getCountryFromPhone } from "@/components/PhoneInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import { API_URL } from "@/config/api";

export default function ClientsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [accountFilter, setAccountFilter] = useState("all"); // "all", "with_account", "without_account"
  const [expandedClient, setExpandedClient] = useState(null);
  const [clientBookings, setClientBookings] = useState({});
  const [loadingBookings, setLoadingBookings] = useState({});
  const [selectedClients, setSelectedClients] = useState([]);
  const [deleting, setDeleting] = useState(false);
  
  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", phone: "", language: "en" });
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem("admin_token");

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchClients();
  }, [token, navigate]);

  // Filter clients based on search query and account filter
  useEffect(() => {
    let filtered = clients;
    
    // Filter by account status
    if (accountFilter === "with_account") {
      filtered = filtered.filter(c => c.has_account === true);
    } else if (accountFilter === "without_account") {
      filtered = filtered.filter(c => c.has_account !== true);
    }
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(client => 
        (client.name && client.name.toLowerCase().includes(query)) ||
        (client.email && client.email.toLowerCase().includes(query)) ||
        (client.phone && client.phone.includes(query))
      );
    }
    
    setFilteredClients(filtered);
  }, [searchQuery, accountFilter, clients]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_URL}/api/admin/clients`, { headers });
      // Filter out deleted clients
      const activeClients = response.data.clients.filter(c => !c.deleted);
      setClients(activeClients);
      setFilteredClients(activeClients);
      setSelectedClients([]);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        localStorage.removeItem("admin_token");
        navigate("/admin");
      } else {
        toast.error("Failed to load clients");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchClientBookings = async (clientEmail) => {
    setLoadingBookings(prev => ({ ...prev, [clientEmail]: true }));
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const encodedEmail = encodeURIComponent(clientEmail);
      const response = await axios.get(`${API_URL}/api/admin/clients/${encodedEmail}/bookings`, { headers });
      setClientBookings(prev => ({ ...prev, [clientEmail]: response.data }));
    } catch (error) {
      console.error("Failed to fetch client bookings:", error);
      toast.error("Failed to load bookings");
    } finally {
      setLoadingBookings(prev => ({ ...prev, [clientEmail]: false }));
    }
  };

  const toggleClientExpand = async (clientEmail) => {
    if (expandedClient === clientEmail) {
      setExpandedClient(null);
    } else {
      setExpandedClient(clientEmail);
      if (!clientBookings[clientEmail]) {
        fetchClientBookings(clientEmail);
      }
    }
  };

  const toggleSelectClient = (email) => {
    setSelectedClients(prev => 
      prev.includes(email) 
        ? prev.filter(e => e !== email)
        : [...prev, email]
    );
  };

  const toggleSelectAll = () => {
    if (selectedClients.length === filteredClients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(filteredClients.map(c => c.email));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedClients.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedClients.length} client(s)? This will NOT affect their booking history.`)) {
      return;
    }
    
    setDeleting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API_URL}/api/admin/clients/bulk-delete`, { ids: selectedClients }, { headers });
      toast.success(`${selectedClients.length} client(s) deleted`);
      fetchClients();
    } catch (error) {
      console.error("Bulk delete failed:", error);
      toast.error("Failed to delete clients");
    } finally {
      setDeleting(false);
    }
  };

  // Edit client functions
  const openEditModal = (client) => {
    setEditingClient(client);
    const phone = client.phone || "+33";
    const detectedLang = getCountryFromPhone(phone)?.lang || "en";
    setEditForm({
      name: client.name || "",
      phone: phone,
      language: client.language || detectedLang
    });
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingClient(null);
    setEditForm({ name: "", phone: "", language: "en" });
  };

  const handleSaveClient = async () => {
    if (!editingClient) return;
    
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(
        `${API_URL}/api/admin/clients/${encodeURIComponent(editingClient.email)}`,
        { name: editForm.name, phone: editForm.phone, language: editForm.language },
        { headers }
      );
      toast.success("Client updated successfully");
      closeEditModal();
      fetchClients();
    } catch (error) {
      console.error("Update failed:", error);
      toast.error("Failed to update client");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin");
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-slate-900 text-white py-4 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-md bg-orange-500 flex items-center justify-center">
                <Anchor className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <span className="text-lg sm:text-xl font-black text-white tracking-tight">Y GO</span>
              <span className="hidden sm:inline text-xs text-slate-400 border-l border-slate-700 pl-3">Client Management</span>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <Link to="/admin/dashboard">
                <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white px-2 sm:px-4">
                  <ArrowLeft className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Back</span>
                </Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-300 hover:text-white px-2 sm:px-4">
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Action Bar */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <h1 className="text-2xl font-bold text-slate-900">Clients ({filteredClients.length})</h1>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <div className="relative flex-1 sm:flex-none">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-full sm:w-48"
              />
            </div>
            <Button variant="outline" size="sm" onClick={fetchClients}>
              <RefreshCw className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            {selectedClients.length > 0 && (
              <Button 
                variant="destructive" 
                size="sm" 
                onClick={handleBulkDelete}
                disabled={deleting}
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Delete ({selectedClients.length})
              </Button>
            )}
          </div>
        </div>

        {/* Account Filter Tabs */}
        <div className="flex items-center gap-2 mb-4">
          <span className="text-sm text-slate-500">Filter:</span>
          <div className="flex rounded-lg border border-slate-200 overflow-hidden">
            <button
              onClick={() => setAccountFilter("all")}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                accountFilter === "all" 
                  ? "bg-orange-500 text-white" 
                  : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              All ({clients.length})
            </button>
            <button
              onClick={() => setAccountFilter("with_account")}
              className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-slate-200 ${
                accountFilter === "with_account" 
                  ? "bg-green-500 text-white" 
                  : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              <UserCheck className="w-3.5 h-3.5 inline mr-1" />
              With Account ({clients.filter(c => c.has_account).length})
            </button>
            <button
              onClick={() => setAccountFilter("without_account")}
              className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-slate-200 ${
                accountFilter === "without_account" 
                  ? "bg-slate-500 text-white" 
                  : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              <UserX className="w-3.5 h-3.5 inline mr-1" />
              No Account ({clients.filter(c => !c.has_account).length})
            </button>
          </div>
        </div>

        {/* Select All */}
        {filteredClients.length > 0 && (
          <div className="mb-4 flex items-center gap-2">
            <Checkbox
              checked={selectedClients.length === filteredClients.length && filteredClients.length > 0}
              onCheckedChange={toggleSelectAll}
            />
            <span className="text-sm text-slate-600">Select all</span>
          </div>
        )}

        {/* Clients List */}
        <div className="space-y-4">
          {filteredClients.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">
                  {searchQuery ? "No clients found matching your search." : "No clients yet. Clients are registered when they make a booking."}
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredClients.map((client) => (
              <Card key={client.email} className={selectedClients.includes(client.email) ? "ring-2 ring-orange-500" : ""}>
                <CardContent className="py-4">
                  {/* Client Info Row */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={selectedClients.includes(client.email)}
                        onCheckedChange={() => toggleSelectClient(client.email)}
                        className="mt-1 h-4 w-4"
                      />
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                        <User className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-bold text-slate-900 truncate">
                            {client.name || "Unknown"}
                          </h3>
                          <Badge variant="outline" className="text-xs shrink-0">
                            {client.booking_count || 0} booking{(client.booking_count || 0) !== 1 ? 's' : ''}
                          </Badge>
                          {client.has_account ? (
                            <Badge className="text-xs shrink-0 bg-green-100 text-green-700 border-green-300 hover:bg-green-100">
                              <UserCheck className="w-3 h-3 mr-1" />
                              Account
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs shrink-0 text-slate-400 border-slate-200">
                              <UserX className="w-3 h-3 mr-1" />
                              No account
                            </Badge>
                          )}
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 mt-1 text-sm text-slate-500">
                          <span className="flex items-center gap-1 truncate">
                            <Mail className="w-4 h-4 shrink-0" />
                            <span className="truncate">{client.email}</span>
                          </span>
                          {client.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-4 h-4 shrink-0" />
                              {client.phone}
                            </span>
                          )}
                        </div>
                        {client.last_booking_at && (
                          <p className="text-xs text-slate-400 mt-1">
                            Last booking: {formatDate(client.last_booking_at)}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2 ml-8 sm:ml-0">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => openEditModal(client)}
                        title="Edit client"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => toggleClientExpand(client.email)}
                        title="View bookings"
                        className="text-xs sm:text-sm"
                      >
                        <History className="w-4 h-4 sm:mr-1" />
                        <span className="hidden sm:inline">History</span>
                        {expandedClient === client.email ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Expanded Bookings */}
                  {expandedClient === client.email && (
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                        <History className="w-4 h-4" />
                        Booking History
                      </h4>
                      
                      {loadingBookings[client.email] ? (
                        <div className="flex justify-center py-4">
                          <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                        </div>
                      ) : clientBookings[client.email]?.bookings?.length > 0 ? (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {clientBookings[client.email].bookings.map((booking) => (
                            <div key={booking.booking_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg text-sm">
                              <div className="flex items-center gap-3">
                                <span className="font-mono font-semibold text-orange-600">{booking.booking_id}</span>
                                <span className="flex items-center gap-1 text-slate-600">
                                  <Ship className="w-3 h-3" />
                                  {booking.ship_name}
                                </span>
                                <span className="text-slate-400">|</span>
                                <span className="text-slate-600">{booking.terminal_name}</span>
                                <span className="text-slate-400">|</span>
                                <span className="text-slate-500">{formatDate(booking.pickup_datetime)}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge className={
                                  booking.status === "completed" ? "bg-green-100 text-green-800" :
                                  booking.status === "cancelled" ? "bg-red-100 text-red-800" :
                                  booking.status === "assigned" ? "bg-blue-100 text-blue-800" :
                                  "bg-amber-100 text-amber-800"
                                }>
                                  {booking.status}
                                </Badge>
                                <Badge className={
                                  booking.payment_status === "paid" ? "bg-purple-100 text-purple-800" :
                                  booking.payment_status === "paid_by_cash" ? "bg-emerald-100 text-emerald-800" :
                                  "bg-amber-100 text-amber-800"
                                }>
                                  {booking.payment_status === "paid" ? "Online" : 
                                   booking.payment_status === "paid_by_cash" ? "Cash" : "Pending"}
                                </Badge>
                                <span className="font-semibold">€{booking.final_price}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-500 text-sm py-4 text-center">No bookings found</p>
                      )}
                      
                      {/* Stats Summary */}
                      {clientBookings[client.email]?.stats && (
                        <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t">
                          <div className="text-center">
                            <p className="text-xs text-slate-500">Total Bookings</p>
                            <p className="text-lg font-bold text-slate-700">{clientBookings[client.email].total}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-slate-500">Total Spent</p>
                            <p className="text-lg font-bold text-emerald-600">€{clientBookings[client.email].stats.total_spent?.toFixed(0) || 0}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-slate-500">Completed</p>
                            <p className="text-lg font-bold text-blue-600">{clientBookings[client.email].stats.completed || 0}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Edit Client Modal */}
      {editModalOpen && editingClient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold">Edit Client</h3>
                <Button variant="ghost" size="sm" onClick={closeEditModal}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-sm text-slate-600">Email (read-only)</Label>
                  <Input value={editingClient.email} disabled className="bg-slate-100 mt-1" />
                </div>
                
                <div>
                  <Label className="text-sm text-slate-600">Name</Label>
                  <Input 
                    value={editForm.name} 
                    onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                    placeholder="Client name"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-sm text-slate-600">Phone</Label>
                  <PhoneInput
                    value={editForm.phone}
                    onChange={(phone) => setEditForm({...editForm, phone})}
                    onCountryChange={(country) => {
                      // Auto-update language when country changes
                      setEditForm(prev => ({...prev, language: country.lang}));
                    }}
                    placeholder="Phone number"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-sm text-slate-600">Notification Language</Label>
                  <p className="text-xs text-slate-400 mb-1">Auto-detected from phone, can be overridden</p>
                  <Select value={editForm.language} onValueChange={(value) => setEditForm({...editForm, language: value})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">🇬🇧 English</SelectItem>
                      <SelectItem value="fr">🇫🇷 French</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex gap-2 mt-6">
                <Button variant="outline" onClick={closeEditModal} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  onClick={handleSaveClient} 
                  disabled={saving}
                  className="flex-1 bg-orange-500 hover:bg-orange-600"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
